import idx from "idx";
import { setCookie } from "../../../../util/auth";
import {
    getProjectSummary,
    getProgressClaimList,
    getOptions,
    saveProjectType
} from "../../../../actions/project-management.action";
import { getResourceAllocations } from "../../../../actions/timesheet.action";
import { PROJECT_TYPE } from "../../../../util/enums";
import { combineData, getVariationNumber } from "../../../../util/common";
import { stageTotal } from "../../../../util/calculations/calculate-stage";
import { optionsTotal } from "../../../../util/calculations/calculate-option";
import { getRevisedProjectTotal } from "../get-revised-calculation";
import {
    formatHoursDisplay,
    getVariance,
    getUtilisationRate,
    getEffortCompletionPercentage,
    getTimesheetHoursMap
} from "../hours-calculation";
import { roundValue, toDecimal } from "../../../../util/decimal-utils";
import {
    addOptionDataAction,
    addProjectAction,
    addProjectTotalAction,
    addRevisedProjectTotalAction,
    addSectionDataAction,
    addProjectVariationAction,
    addSectionVariationAction,
    addOptionsListAction,
    addProgressClaimsAction,
    addResourceAllocationsAction,
    addSectionsAction,
    addVariationDataAction,
    addOptionsAction
} from "../../../../slice/project-summary";
import { getLevels } from "./helper/get-levels";
import attachSubtasksToTasks from "./helper/attach-subtasks-to-tasks";
import calculateHoursRecursively from "./helper/calculate-hours-recursively";
import calculateTotalProjectHours from "./helper/calculate-total-project-hours";
import calculateVariationHours from "./helper/calculate-variation-hours";


/**
 * Performs a forced fresh update of project summary data and financial calculations.
 *
 * @param {Object} params - Parameters object
 * @param {string} params.projectId - Unique project identifier
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {string|null} [params.type=null] - Project view/data type
 * @param {boolean} [params.forceFresh=true] - Force fresh API execution flag
 * @returns {Promise<Object|undefined>} Aggregated summary data
 */
export const updateProjectSummaryData = async ({ projectId, dispatch, type = null, forceFresh = true }) => {
    if (!projectId) return;
    let totalApprovedInvoices = 0;
    let showStageSelect = false;



    setCookie({ name: "projectId", value: projectId });

    const isTender = type === PROJECT_TYPE.TENDER;
    let totalClaimAmount = 0;
    let progressClaims = [];
    let timesheetEntries = [];
    let subtasks = [];
    let optionList = [];

    if (isTender) {
        optionList = await dispatch(getOptions(projectId, type ? { type } : {}));
    } else {
        const [pc, ts, opt] = await Promise.all([
            dispatch(getProgressClaimList(projectId)),
            dispatch(getResourceAllocations({ projectId })),
            dispatch(getOptions(projectId, type ? { type } : {})),
        ]);
        progressClaims = Array.isArray(pc) ? pc : [];
        timesheetEntries = Array.isArray(ts) ? ts : [];
        optionList = opt;

        progressClaims.forEach((claims) => {
            if (claims.progressClaimStatus && claims.progressClaimStatus != 'Draft') {
                totalClaimAmount += claims.claimAmount;
                totalApprovedInvoices++;
            }
        });
    }

    dispatch(getProjectSummary(projectId)).then((projectData) => {
        // Save the project type to Redux and cookies
        if (projectData.projectType) {
            dispatch(saveProjectType(projectData.projectType));
            setCookie({ name: "projectType", value: projectData.projectType });
        }

        getLevels({ levelId: projectData.level, dispatch });
        // 0. Extract variation and option data
        const isProjectView = type === 'project-view';
        const optionData = getVariationNumber(optionList);
        const variationData = isProjectView
            ? optionData.filter(_ => _.proposal_status === 'APPROVED')
            : optionData.filter(_ => !['REJECTED', 'CANCELLED'].includes(_.proposal_status));
        const projectLevelVariation = variationData.filter(_ => !_.stageId);
        const sectionLevelVariation = variationData.filter(_ => _.stageId);

        // 1. Apply hours calculation to all variations FIRST to ensure totals are accurate
        const enhancedVariationData = calculateVariationHours(variationData, timesheetEntries, subtasks);
        const enhancedOptionData = calculateVariationHours(optionData, timesheetEntries, subtasks);
        const enhancedProjectLevelVariation = calculateVariationHours(projectLevelVariation, timesheetEntries, subtasks);
        const enhancedSectionLevelVariation = calculateVariationHours(sectionLevelVariation, timesheetEntries, subtasks);

        // 2. Now calculate option totals with enhanced hours data
        const optionCalculation = optionsTotal({ options: enhancedOptionData, projectType: projectData.type });
        const approvedOptionCalculation = isProjectView
            ? optionsTotal({ options: enhancedOptionData.filter(_ => _.proposal_status === 'APPROVED'), projectType: projectData.type })
            : optionCalculation;

        // 3. Add hours display formatting and aggregates for the Variation Total row in UI
        if (optionCalculation && optionCalculation.projectTotal) {
            optionCalculation.projectTotal.forecastHoursDisplay = formatHoursDisplay(optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.actualHoursDisplay = formatHoursDisplay(optionCalculation.projectTotal.actualHours);
            optionCalculation.projectTotal.variance = getVariance(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.utilisationRate = getUtilisationRate(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.effortCompletionPercentageValue = getEffortCompletionPercentage(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.effortCompletionPercentage = `${optionCalculation.projectTotal.effortCompletionPercentageValue.toFixed(2)}%`;
        }
        if (approvedOptionCalculation && approvedOptionCalculation.projectTotal && approvedOptionCalculation !== optionCalculation) {
            approvedOptionCalculation.projectTotal.forecastHoursDisplay = formatHoursDisplay(approvedOptionCalculation.projectTotal.forecastHours);
            approvedOptionCalculation.projectTotal.actualHoursDisplay = formatHoursDisplay(approvedOptionCalculation.projectTotal.actualHours);
            approvedOptionCalculation.projectTotal.variance = getVariance(approvedOptionCalculation.projectTotal.actualHours, approvedOptionCalculation.projectTotal.forecastHours);
            approvedOptionCalculation.projectTotal.utilisationRate = getUtilisationRate(approvedOptionCalculation.projectTotal.actualHours, approvedOptionCalculation.projectTotal.forecastHours);
            approvedOptionCalculation.projectTotal.effortCompletionPercentageValue = getEffortCompletionPercentage(approvedOptionCalculation.projectTotal.actualHours, approvedOptionCalculation.projectTotal.forecastHours);
            approvedOptionCalculation.projectTotal.effortCompletionPercentage = `${approvedOptionCalculation.projectTotal.effortCompletionPercentageValue.toFixed(2)}%`;
        }

        let stages = idx(projectData, _ => _.stages) || [];
        const sectionData = combineData({ stages, variation: enhancedVariationData }) || [];


        const processStages = (stagesData) => {
            if (Array.isArray(stagesData)) {
                return stagesData.forEach((stage) => {
                    if (stage.stages) {
                        processStages(stage.stages);
                    } else if (stage.stages === null && stage.tasks) {
                        stage.tasks.forEach((task, index) => {
                            (task.material || []).forEach((material, mIndex) => {
                                if (typeof material === 'string') {
                                    task.material[mIndex] = JSON.parse(material);
                                }
                            });
                        });
                    }
                });
            }
        }


        const stageData = stageTotal(sectionData);
        let revisedProjectTotal = getRevisedProjectTotal({ sectionProjectTotal: stageData.projectTotal, optionProjectTotal: approvedOptionCalculation.projectTotal });

        if (projectData.stages !== null) processStages(projectData.stages);

        const sectionDataWithSubtasks = attachSubtasksToTasks(sectionData, subtasks);
        const hoursMap = getTimesheetHoursMap(timesheetEntries);
        const enhancedSectionData = calculateHoursRecursively(sectionDataWithSubtasks, timesheetEntries, hoursMap);

        // Map recursive hour calculations back to the summarized sections object
        const mapHoursToSections = (sections, enhancedStages) => {
            enhancedStages.forEach(enhancedStage => {
                if (sections[enhancedStage.id]) {
                    sections[enhancedStage.id] = {
                        ...sections[enhancedStage.id],
                        forecastHours: enhancedStage.forecastHours,
                        actualHours: enhancedStage.actualHours,
                        forecastHoursDisplay: enhancedStage.forecastHoursDisplay,
                        actualHoursDisplay: enhancedStage.actualHoursDisplay,
                        variance: enhancedStage.variance,
                        utilisationRate: enhancedStage.utilisationRate,
                        effortCompletionPercentageValue: enhancedStage.effortCompletionPercentageValue,
                        effortCompletionPercentage: enhancedStage.effortCompletionPercentage,
                        tasks: enhancedStage.tasks || []
                    };
                }
                if (enhancedStage.stages && enhancedStage.stages.length > 0) {
                    mapHoursToSections(sections, enhancedStage.stages);
                }
            });
        };
        mapHoursToSections(stageData.sections, enhancedSectionData);

        // 1. Build a global tasks hours map from the recursive calculation results
        const globalTaskHoursMap = {};
        const buildGlobalTaskHoursMap = (enhancedStages) => {
            enhancedStages.forEach(stage => {
                if (stage.tasks && Array.isArray(stage.tasks)) {
                    stage.tasks.forEach(task => {
                        globalTaskHoursMap[task.id] = {
                            forecastHours: task.forecastHours,
                            actualHours: task.actualHours,
                            forecastHoursDisplay: task.forecastHoursDisplay,
                            actualHoursDisplay: task.actualHoursDisplay,
                            variance: task.variance,
                            utilisationRate: task.utilisationRate,
                            effortCompletionPercentageValue: task.effortCompletionPercentageValue,
                            effortCompletionPercentage: task.effortCompletionPercentage,
                        };
                    });
                }
                if (stage.stages && stage.stages.length > 0) {
                    buildGlobalTaskHoursMap(stage.stages);
                }
            });
        };
        buildGlobalTaskHoursMap(enhancedSectionData);

        // 2. Enrich individual tasks stored within each section's taskList
        Object.keys(stageData.sections).forEach(sectionId => {
            const section = stageData.sections[sectionId];
            if (section.taskList && section.taskList.tasks) {
                Object.keys(section.taskList.tasks).forEach(taskId => {
                    if (globalTaskHoursMap[taskId]) {
                        section.taskList.tasks[taskId] = {
                            ...section.taskList.tasks[taskId],
                            ...globalTaskHoursMap[taskId]
                        };
                    }
                });
            }
        });

        // Calculate projectTotal and revisedProjectTotal hours
        const { plannedEffort, actualHours } = calculateTotalProjectHours(enhancedSectionData);
        let totalProjectPlannedEffort = plannedEffort;
        let totalProjectActualHours = actualHours;

        const projectTotalVariance = getVariance(totalProjectActualHours, totalProjectPlannedEffort);
        const projectTotalUtilisationRate = getUtilisationRate(totalProjectActualHours, totalProjectPlannedEffort);
        const projectTotalEffortCompletionPercentage = getEffortCompletionPercentage(totalProjectActualHours, totalProjectPlannedEffort);

        // Update projectTotal with hours
        stageData.projectTotal = {
            ...stageData.projectTotal,
            forecastHours: totalProjectPlannedEffort,
            actualHours: totalProjectActualHours,
            forecastHoursDisplay: formatHoursDisplay(totalProjectPlannedEffort),
            actualHoursDisplay: formatHoursDisplay(totalProjectActualHours),
            variance: roundValue(projectTotalVariance, 0),
            utilisationRate: roundValue(projectTotalUtilisationRate, 0),
            effortCompletionPercentageValue: projectTotalEffortCompletionPercentage,
            effortCompletionPercentage: `${projectTotalEffortCompletionPercentage.toFixed(2)}%`
        };

        // Recalculate projectTotal's percentageComplete based on Financials (Total Claimed / Contract),
        // mirroring the revisedProjectTotal fix below, so the "Project Total" row (which reads from
        // stageData.projectTotal, not revisedProjectTotal) also reflects approved progress claims.
        if (toDecimal(stageData.projectTotal.totalSaleAmount).gt(0)) {
            const projectTotalFinancialPercentage = roundValue(
                toDecimal(totalClaimAmount).dividedBy(toDecimal(stageData.projectTotal.totalSaleAmount)).times(100)
            );
            stageData.projectTotal.percentageComplete = projectTotalFinancialPercentage;
            stageData.projectTotal.completionPercentage = `${roundValue(projectTotalFinancialPercentage, 0)}%`;
        }

        // Enrich variation hours into option stage summary and project total
        if (optionCalculation) {
            // 1. Enrich stage summaries (Variation Total rows)
            if (optionCalculation.stageSummary) {
                Object.keys(optionCalculation.stageSummary).forEach(stageId => {
                    const summary = optionCalculation.stageSummary[stageId];
                    const f = summary.forecastHours || 0;
                    const a = summary.actualHours || 0;
                    const v = getVariance(a, f);
                    const u = getUtilisationRate(a, f);
                    const c = getEffortCompletionPercentage(a, f);

                    optionCalculation.stageSummary[stageId] = {
                        ...summary,
                        forecastHoursDisplay: formatHoursDisplay(f),
                        actualHoursDisplay: formatHoursDisplay(a),
                        variance: roundValue(v, 0),
                        utilisationRate: roundValue(u, 0),
                        effortCompletionPercentageValue: c,
                        effortCompletionPercentage: `${c.toFixed(2)}%`,
                    };

                    // 2. Enrich individual variations within this stage's optionList (Object map)
                    if (summary.optionList && typeof summary.optionList === 'object') {
                        Object.keys(summary.optionList).forEach(optionId => {
                            const variationItem = summary.optionList[optionId];
                            const enhancedV = enhancedVariationData.find(v => String(v.id) === String(optionId));
                            if (enhancedV) {
                                Object.assign(variationItem, {
                                    forecastHours: enhancedV.forecastHours,
                                    actualHours: enhancedV.actualHours,
                                    forecastHoursDisplay: enhancedV.forecastHoursDisplay,
                                    actualHoursDisplay: enhancedV.actualHoursDisplay,
                                    variance: enhancedV.variance,
                                    utilisationRate: enhancedV.utilisationRate,
                                    effortCompletionPercentageValue: enhancedV.effortCompletionPercentageValue,
                                    effortCompletionPercentage: enhancedV.effortCompletionPercentage,
                                });
                            }
                        });
                    }
                });
            }

            // 3. Enrich overall options project total
            const of = optionCalculation.projectTotal?.forecastHours || 0;
            const oa = optionCalculation.projectTotal?.actualHours || 0;
            const ov = getVariance(oa, of);
            const ou = getUtilisationRate(oa, of);
            const oc = getEffortCompletionPercentage(oa, of);

            optionCalculation.projectTotal = {
                ...optionCalculation.projectTotal,
                forecastHoursDisplay: formatHoursDisplay(of),
                actualHoursDisplay: formatHoursDisplay(oa),
                variance: roundValue(ov, 0),
                utilisationRate: roundValue(ou, 0),
                effortCompletionPercentageValue: oc,
                effortCompletionPercentage: `${oc.toFixed(2)}%`,
            };

            if (approvedOptionCalculation && approvedOptionCalculation.projectTotal && approvedOptionCalculation !== optionCalculation) {
                const aof = approvedOptionCalculation.projectTotal?.forecastHours || 0;
                const aoa = approvedOptionCalculation.projectTotal?.actualHours || 0;
                const aov = getVariance(aoa, aof);
                const aou = getUtilisationRate(aoa, aof);
                const aoc = getEffortCompletionPercentage(aoa, aof);

                approvedOptionCalculation.projectTotal = {
                    ...approvedOptionCalculation.projectTotal,
                    forecastHoursDisplay: formatHoursDisplay(aof),
                    actualHoursDisplay: formatHoursDisplay(aoa),
                    variance: roundValue(aov, 0),
                    utilisationRate: roundValue(aou, 0),
                    effortCompletionPercentageValue: aoc,
                    effortCompletionPercentage: `${aoc.toFixed(2)}%`,
                };
            }

            if (isProjectView && approvedOptionCalculation) {
                optionCalculation.approvedProjectTotal = approvedOptionCalculation.projectTotal;
            }
        }

        // Add hours from ALL variations (approved) to totals for the Revised Project Total
        let finalPlannedEffort = toDecimal(totalProjectPlannedEffort);
        let finalActualHours = toDecimal(totalProjectActualHours);

        enhancedVariationData.forEach(variation => {
            finalPlannedEffort = finalPlannedEffort.plus(toDecimal(variation.forecastHours || 0));
            finalActualHours = finalActualHours.plus(toDecimal(variation.actualHours || 0));
        });

        const revisedProjectTotalVariance = getVariance(finalActualHours.toNumber(), finalPlannedEffort.toNumber());
        const revisedProjectTotalUtilisationRate = getUtilisationRate(finalActualHours.toNumber(), finalPlannedEffort.toNumber());
        const revisedProjectTotalEffortCompletionPercentage = getEffortCompletionPercentage(finalActualHours.toNumber(), finalPlannedEffort.toNumber());

        // Update revisedProjectTotal with summed hours from both project and variations
        revisedProjectTotal = {
            ...revisedProjectTotal,
            forecastHours: finalPlannedEffort.toNumber(),
            actualHours: finalActualHours.toNumber(),
            forecastHoursDisplay: formatHoursDisplay(finalPlannedEffort.toNumber()),
            actualHoursDisplay: formatHoursDisplay(finalActualHours.toNumber()),
            variance: roundValue(revisedProjectTotalVariance, 0),
            utilisationRate: roundValue(revisedProjectTotalUtilisationRate, 0),
            effortCompletionPercentageValue: revisedProjectTotalEffortCompletionPercentage,
            effortCompletionPercentage: `${revisedProjectTotalEffortCompletionPercentage.toFixed(2)}%`
        };

        // Recalculate percentageComplete based on Financials (Total Claimed / Revised Contract)
        // This ensures Section-level claims are captured even if task percentages aren't updated
        if (toDecimal(revisedProjectTotal.totalSaleAmount).gt(0)) {
            const financialPercentage = roundValue(
                toDecimal(totalClaimAmount).dividedBy(toDecimal(revisedProjectTotal.totalSaleAmount)).times(100)
            );
            revisedProjectTotal.percentageComplete = financialPercentage;
            revisedProjectTotal.completionPercentage = `${roundValue(financialPercentage, 0)}%`;
        }

        // Also update stage-level percentageComplete if it's 0 but there are claims
        // This fixes the issue where an Attributed Section has claims but 0% task completion
        if (progressClaims && Array.isArray(progressClaims)) {
            const sectionClaimsDataByStage = {};

            // 1. Build a taskId -> stageId mapping from sectionData for task-level claims
            const taskToStageMap = {};
            const buildTaskStageMapping = (stagesArray) => {
                stagesArray.forEach(stage => {
                    if (stage.stages && Array.isArray(stage.stages)) {
                        buildTaskStageMapping(stage.stages);
                    } else if (stage.tasks && Array.isArray(stage.tasks)) {
                        stage.tasks.forEach(task => {
                            if (task.id) {
                                taskToStageMap[task.id] = stage.id;
                            }
                        });
                    }
                });
            };
            buildTaskStageMapping(sectionData);

            // 2. Build parent-child relationships
            const stageParentMap = {};
            const buildParentMap = (stagesArray, parentId = null) => {
                stagesArray.forEach(stage => {
                    if (parentId) {
                        stageParentMap[stage.id] = parentId;
                    }
                    if (stage.stages && Array.isArray(stage.stages)) {
                        buildParentMap(stage.stages, stage.id);
                    }
                });
            };
            buildParentMap(sectionData);

            // 3. Helper to get all ancestors for a given stageId (including the stage itself)
            const getAllAncestors = (stageId) => {
                const ancestors = [stageId];
                let currentId = stageId;
                while (stageParentMap[currentId]) {
                    ancestors.push(stageParentMap[currentId]);
                    currentId = stageParentMap[currentId];
                }
                return ancestors;
            };

            // 4. Aggregate claims by stageId
            progressClaims.forEach(claim => {
                const tasks = claim.progressClaimTask || claim.tbl_progress_claim_tasks;
                if (claim.progressClaimStatus && claim.progressClaimStatus != 'Draft' && Array.isArray(tasks)) {
                    // Protections against double counting within a single claim
                    const stagesWithSectionClaims = new Set(
                        tasks.filter(t => !t.isDelete && t.type === 'SECTION').map(t => t.stageId)
                    );

                    tasks.filter(t => !t.isDelete).forEach(taskClaim => {
                        let targetStageId = null;
                        if (taskClaim.stageId) {
                            targetStageId = taskClaim.stageId;
                        } else if (taskClaim.taskId) {
                            targetStageId = taskToStageMap[taskClaim.taskId];
                        }

                        if (targetStageId) {
                            // Accuracy check: if this is a task claim, only count it if its stage doesn't have a section claim
                            if (taskClaim.type === 'SECTION' || !stagesWithSectionClaims.has(taskClaim.stageId)) {
                                const claimAmount = parseFloat(taskClaim.total) || 0;
                                const ancestors = getAllAncestors(targetStageId);
                                ancestors.forEach(ancestorId => {
                                    sectionClaimsDataByStage[ancestorId] = (sectionClaimsDataByStage[ancestorId] || 0) + claimAmount;
                                });
                            }
                        }
                    });
                }
            });

            // 5. Update ALL section percentages using financial completion (claim / sale)
            if (stageData.sections) {
                Object.keys(stageData.sections).forEach(sectionId => {
                    const section = stageData.sections[sectionId];
                    const claimTotal = sectionClaimsDataByStage[sectionId] || 0;

                    if (section.totalSaleAmount > 0 && claimTotal > 0) {
                        const financialPercentage = Math.min(roundValue((claimTotal / section.totalSaleAmount) * 100), 100);
                        section.percentageComplete = financialPercentage;
                        // Non-nested sections render from taskList.total (see project-task-list.jsx
                        // hasNestedStages check), so this must be kept in sync too.
                        if (section.taskList && section.taskList.total) {
                            section.taskList.total.percentageComplete = financialPercentage;
                        }
                        // Propagate down to each individual task row too - otherwise the "Total"/
                        // section-level completion % is fixed but each task row (which reads its own
                        // taskList.tasks[taskId].percentageComplete, an unrelated stale field from the
                        // task record) keeps showing its old, pre-claim value even though the Budget
                        // page (which derives per-task completion live from claim data) shows 100%.
                        if (section.taskList && section.taskList.tasks) {
                            Object.values(section.taskList.tasks).forEach(task => {
                                task.percentageComplete = financialPercentage;
                            });
                        }
                    }
                });
            }
        }

        dispatch(addSectionDataAction(enhancedSectionData));
        dispatch(addOptionDataAction(enhancedOptionData));
        dispatch(addVariationDataAction(enhancedVariationData));
        dispatch(addProjectVariationAction(enhancedProjectLevelVariation));
        dispatch(addSectionVariationAction(enhancedSectionLevelVariation));
        dispatch(addProjectTotalAction(stageData.projectTotal));
        dispatch(addRevisedProjectTotalAction(revisedProjectTotal));
        dispatch(addSectionsAction(stageData.sections));
        dispatch(addOptionsAction(optionCalculation));
        if (Array.isArray(progressClaims) && progressClaims.length > 0) {
            dispatch(addProgressClaimsAction(progressClaims));
        }
        if (Array.isArray(optionList) && optionList.length > 0) {
            dispatch(addOptionsListAction(optionList));
        }
        if (Array.isArray(timesheetEntries) && timesheetEntries.length > 0) {
            dispatch(addResourceAllocationsAction(timesheetEntries));
        }

        delete projectData.stages;

        dispatch(addProjectAction({
            projectId: projectData.id,
            projectName: projectData.name,
            projectManagerName: idx(projectData, _ => _.manager.name),
            quoteType: projectData.quoteType,
            projectDescription: projectData.description,
            customerName: projectData.customer && projectData.customer.businessName,
            hasStages: projectData.hasStages,
            projectType: projectData.type,
            projectDataType: projectData.projectType,
            level: projectData.level,
            customerLevel: projectData.customer ? projectData.customer.level : "",
            contactData: projectData.customerContact,
            projectMarginValue: projectData.profitMarginValue || "",
            siteAddress: projectData.address,
            projectDueDate: projectData.dateOfCreation,
            attachmentArr: projectData.attachment,
            showStageSelect,
            totalApprovedInvoices,
            res: projectData
        }))

    });
}